import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { styled } from '@mui/system';
import MuiSelect from '@mui/material/Select';
import MuiMenuItem from '@mui/material/MenuItem';
import CustomInputBase from '@cpg-its/ads.form.custom-input-base';
import { black, blue, greyBorder, darkBlue } from '@cpg-its/ads.constants.colors';
const selectStyles = ({ backgroundColor, color, multipleHeight, optionsColor }) => ({
    multiple: {
        '& .MuiNativeSelect-select': {
            height: multipleHeight || 'auto',
            padding: '0.5rem',
        },
    },
    narrow: {
        height: '2rem',
        '& input, .MuiSelect-root': {
            height: '1rem',
            margin: '2px',
        },
    },
    selectColor: {
        color: color || black,
        '& .MuiSelect-icon': {
            color,
        },
    },
    selectBackground: {
        backgroundColor: `${backgroundColor} !important`,
        border: `1px solid ${backgroundColor} !important`,
        '&:focus, &:hover': {
            backgroundColor: `${backgroundColor} !important`,
            border: `1px solid ${backgroundColor} !important`,
        },
        '& .MuiSelect-select': {
            border: `1px solid ${backgroundColor} !important`,
        },
    },
    selectMultipleHeight: {
        height: 'calc(100% - 1.375rem) !important',
    },
    thickBorder: {
        border: `2px solid ${color || blue} !important`,
    },
    menuItem: {
        color: optionsColor,
    },
    actions: {
        borderBottom: `1px solid ${greyBorder}`,
    },
});
const StyledSelect = styled(MuiSelect)({
    width: '100%',
    padding: 0,
    '& .Mui-icon': {
        marginRight: '0.75rem',
        color: 'inherit',
        '&$disabled': {
            color: black,
        },
    },
    '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        padding: '0 0 0 0.5rem',
        height: 'calc(100% - 4px) !important',
        margin: '0 1px',
        borderRadius: '3px',
    },
    '&.Mui-focused': {
        '& .MuiSelect-select': {
            border: `1px solid ${darkBlue}`,
        },
    },
    '& .Mui-disabled': {
        border: 'none',
    },
});
const StyledMenuItem = styled(MuiMenuItem)({
    '&.Mui-selected': {
        backgroundColor: 'rgba(0, 0, 0, 0.08)',
    },
});
export const SelectComponent = ({ backgroundColor = '', beenTouched = false, color = '', disabled = false, errorStyles = {}, hasActions = false, hasError = false, keepOpen = false, multiple = false, multipleHeight = '', name = '', narrow = false, native = false, noDefault = false, onBlur = null, onChange = null, onFocus = null, options = [], optionsColor = black, overrideStyles = () => ({}), placeholder = '', setFieldTouched = null, setFieldValue = null, testId = undefined, thickBorder = false, value = '', }) => {
    const [open, setOpen] = useState(false);
    const overrideClasses = overrideStyles({
        color,
    });
    const selectClasses = selectStyles({
        optionsColor,
        backgroundColor,
        color,
        multipleHeight,
    });
    const colorClass = (() => {
        if (color) {
            if (Object.keys(overrideClasses).includes('color')) {
                return overrideClasses.color;
            }
            return selectClasses.selectColor;
        }
        return '';
    })();
    const handleBlur = (event) => {
        if (onBlur) {
            onBlur(event);
        }
        else if (setFieldTouched) {
            setFieldTouched(name, true);
        }
    };
    const handleChange = (event) => {
        if (onChange) {
            onChange(event);
        }
        else if (setFieldValue) {
            if (multiple) {
                const selections = event.target.options;
                const selectedVals = [];
                for (let i = 0, l = options.length; i < l; i += 1) {
                    if (selections[i].selected) {
                        selectedVals.push(selections[i].value);
                    }
                }
                setFieldValue(name, selectedVals);
            }
            else {
                setFieldValue(name, event.target.value);
            }
        }
    };
    const handleClose = (event) => {
        if (setFieldTouched) {
            setFieldTouched(name, true);
        }
        if (keepOpen && !event.target.className.includes('MuiBackdrop')) {
            setOpen(true);
        }
        else {
            setOpen(false);
        }
    };
    const handleFocus = (event) => {
        if (onFocus) {
            onFocus(event);
        }
    };
    const handleOpen = () => setOpen(true);
    const configuredOptions = (() => {
        let newOptions = [];
        if (!Array.isArray(options)) {
            const texts = Object.values(options);
            const values = Object.keys(options);
            texts.forEach((string, i) => (newOptions.push({
                text: string,
                value: values[i],
            })));
        }
        else {
            newOptions = options;
        }
        return newOptions;
    })();
    const dropdownOptions = (() => (configuredOptions.map((option) => {
        if (option) {
            const optionValue = option.value || option;
            const optionText = option.text || option;
            if (native) {
                return (_jsx("option", { value: optionValue, children: optionText }, optionValue));
            }
            return (_jsx(StyledMenuItem, { sx: [
                    hasActions ? selectClasses.actions : {},
                    selectClasses.menuItem,
                ], disabled: !!option.disabled, value: optionValue, onClick: keepOpen ? () => setOpen(true) : () => { }, "data-listitem": testId, children: optionText }, optionValue));
        }
        return null;
    })))();
    const checkValue = () => {
        if (!multiple) {
            let newValue = '';
            configuredOptions.forEach((option) => {
                if (option.value === value || option === value) {
                    newValue = value;
                }
            });
            return newValue;
        }
        return value;
    };
    const renderPlaceholder = (() => {
        if (!noDefault) {
            const placeholderText = placeholder || '- Please Select -';
            if (native) {
                return (_jsx("option", { value: "", children: placeholderText }));
            }
            return (_jsx(StyledMenuItem, { sx: hasActions ? selectClasses.actions : {}, value: "", children: placeholderText }));
        }
        return null;
    })();
    return (_jsxs(StyledSelect, { sx: [
            backgroundColor ? selectClasses.selectBackground : {},
            multipleHeight ? selectClasses.selectMultipleHeight : {},
        ], disabled: disabled, "data-formdata": name, input: (_jsx(CustomInputBase, { sx: [
                colorClass,
                thickBorder ? overrideClasses.thickBorder : {},
                beenTouched && hasError ? errorStyles.errorInput : {},
                narrow ? selectClasses.narrow : {},
                multiple ? selectClasses.multiple : {},
            ], inputProps: {
                'data-testid': testId,
            }, fullWidth: true })), multiple: multiple, native: native, open: open, onOpen: handleOpen, onBlur: handleBlur, onChange: handleChange, onClose: handleClose, onFocus: handleFocus, value: checkValue(), displayEmpty: true, children: [renderPlaceholder, dropdownOptions] }));
};
export default SelectComponent;
//# sourceMappingURL=select.js.map